import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip"

import { Label } from "@/components/ui/label";
import { useState, useMemo, useCallback } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Info } from "lucide-react";

const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

const DOB = ({ form }: { form: any }) => {
    const [error, setError] = useState("");

    const yearItems = useMemo(() => {
        return Array.from({ length: 80 }, (_, i) => (
            <SelectItem key={2025 - i} value={String(2025 - i)}>
                {2025 - i}
            </SelectItem>
        ));
    }, []);

    const monthItems = useMemo(() => {
        return months.map((m) => (
            <SelectItem key={m.value} value={m.value}>
                {m.label}
            </SelectItem>
        ));
    }, []);

    const validateInputs = useCallback((monthValue: string, yearValue: string) => {
        if (!monthValue || !yearValue) {
            setError("Please select both month and year");
            return false;
        }
        setError("");
        return true;
    }, []);

    const renderMonths = () => {
        return (
            <FormField
                control={form.control}
                name="month"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-black text-base">Month</FormLabel>
                        <Select
                            value={field.value}
                            onValueChange={(value: string) => {
                                field.onChange(value);
                            }}
                        >
                            <FormControl>
                                <SelectTrigger
                                    id="month-select"
                                    className={`w-full rounded-lg text-gray-500 ${form.formState.errors.month ? 'border-red-500 focus:ring-red-500' : ''
                                        }`}
                                >
                                    <SelectValue placeholder="Month" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                {monthItems}
                            </SelectContent>
                        </Select>
                        <FormMessage className="text-red-500 text-sm mt-1" />
                    </FormItem>
                )}
            />
        );
    };

    const renderYears = () => {
        return (
            <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-black text-base">Year</FormLabel>
                        <Select
                            value={field.value}
                            onValueChange={(value: string) => {
                                field.onChange(value);
                            }}
                        >
                            <FormControl>
                                <SelectTrigger
                                    id="year-select"
                                    className={`w-full rounded-lg px-[12px] py-[10px] text-gray-500 ${form.formState.errors.year ? 'border-red-500 focus:ring-red-500' : ''}`}
                                >
                                    <SelectValue placeholder="Year" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                <ScrollArea className="h-[300px]">
                                    {yearItems}
                                </ScrollArea>
                            </SelectContent>
                        </Select>
                        <FormMessage className="text-red-500 text-sm mt-1" />
                    </FormItem>
                )}
            />
        );
    };

    return (
        <div>
            <div className="flex gap-[4px] items-center mb-[10px]">
                <Label className="text-black text-base">Date of Birth:</Label>
                <Tooltip>
                    <TooltipTrigger><Info className="w-[16px] h-[16px] text-gray-500" /></TooltipTrigger>
                    <TooltipContent>
                        We only need your age to verify that you can legally use our service.
                    </TooltipContent>
                </Tooltip>
            </div>
            <div className="flex gap-[10px]">
                <div className="flex-1">
                    {renderMonths()}
                </div>
                <div className="flex-1">
                    {renderYears()}
                </div>
            </div>
        </div>
    );
};

export default DOB;